/**
 * Everything about a connection that is not a credential: which workspace it
 * belongs to, the tags it is filed under, and the two policies that decide how
 * hard it may be driven.
 *
 * Shared by the connect page and the detail page on purpose — what you can set
 * while creating a connection is exactly what you can change afterwards, and a
 * second copy is how those two quietly stop matching.
 */

import { useState } from 'react'
import type { CircuitBreakerPolicy, RateLimitPolicy } from '@/core/types/api.types'

export const DEFAULT_RATE_LIMIT: RateLimitPolicy = {
  enabled: false,
  requests: 60,
  per_seconds: 60,
  concurrency: 4,
}

export const DEFAULT_CIRCUIT_BREAKER: CircuitBreakerPolicy = {
  enabled: false,
  failure_threshold: 5,
  cooldown_seconds: 60,
}

/** The editable, non-credential half of a connection. */
export interface ConnectionSettingsValue {
  workspaceId: string
  tags: string[]
  rateLimit: RateLimitPolicy
  circuitBreaker: CircuitBreakerPolicy
}

export const emptySettings = (): ConnectionSettingsValue => ({
  workspaceId: '',
  tags: [],
  rateLimit: { ...DEFAULT_RATE_LIMIT },
  circuitBreaker: { ...DEFAULT_CIRCUIT_BREAKER },
})

/** The wire shape the API expects, from the form's shape. */
export function toSettingsPayload(value: ConnectionSettingsValue) {
  return {
    workspace_id: value.workspaceId.trim(),
    tags: value.tags,
    rate_limit: value.rateLimit,
    circuit_breaker: value.circuitBreaker,
  }
}

export function WorkspaceField({
  value,
  onChange,
  options,
}: {
  value: string
  onChange: (next: string) => void
  /** Workspaces already in use, offered as suggestions rather than a fixed list. */
  options?: string[]
}) {
  return (
    <div className="field">
      <label className="field-label" htmlFor="conn-workspace">
        Workspace
      </label>
      <input
        id="conn-workspace"
        className="input"
        list="conn-workspace-options"
        value={value}
        placeholder="Select workspace"
        onChange={(e) => onChange(e.target.value)}
      />
      {options && options.length > 0 && (
        <datalist id="conn-workspace-options">
          {options.map((w) => (
            <option key={w} value={w} />
          ))}
        </datalist>
      )}
      <p className="field-help">
        Optional. Groups this connection with others so the connections list can be filtered down
        to one team or environment.
      </p>
    </div>
  )
}

export function TagsField({
  value,
  onChange,
  suggestions,
}: {
  value: string[]
  onChange: (next: string[]) => void
  suggestions?: string[]
}) {
  const [draft, setDraft] = useState('')

  const add = (raw: string) => {
    const tag = raw.trim()
    if (!tag) return
    // Case-insensitive, because "Prod" and "prod" filing a connection in two
    // places is a filter that quietly misses half of what it should match.
    if (value.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      setDraft('')
      return
    }
    onChange([...value, tag])
    setDraft('')
  }

  const unused = (suggestions ?? []).filter(
    (s) => !value.some((t) => t.toLowerCase() === s.toLowerCase()),
  )

  return (
    <div className="field">
      <label className="field-label" htmlFor="conn-tags">
        Tags
      </label>

      {value.length > 0 && (
        <div className="chiprow" style={{ marginBottom: 6 }}>
          {value.map((tag) => (
            <span key={tag} className="badge badge-neutral">
              {tag}
              <button
                type="button"
                className="tag-x"
                aria-label={`Remove tag ${tag}`}
                onClick={() => onChange(value.filter((t) => t !== tag))}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}

      <input
        id="conn-tags"
        className="input"
        value={draft}
        placeholder="Select tags"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault()
            add(draft)
          } else if (e.key === 'Backspace' && !draft && value.length) {
            onChange(value.slice(0, -1))
          }
        }}
        onBlur={() => add(draft)}
      />

      {unused.length > 0 && (
        <div className="chiprow" style={{ marginTop: 6 }}>
          {unused.slice(0, 8).map((tag) => (
            <button
              key={tag}
              type="button"
              className="chip-filter"
              onClick={() => onChange([...value, tag])}
            >
              + {tag}
            </button>
          ))}
        </div>
      )}

      <p className="field-help">Press Enter to add. Used to filter the connections list.</p>
    </div>
  )
}

/**
 * A policy section that is a toggle first and a form second. Collapsed until
 * it is switched on, because a disabled policy's numbers are noise.
 */
function PolicySection({
  id,
  title,
  summary,
  enabled,
  onToggle,
  children,
}: {
  id: string
  title: string
  summary: string
  enabled: boolean
  onToggle: (next: boolean) => void
  children: React.ReactNode
}) {
  return (
    <div className="policy">
      <label className="switch-row" htmlFor={id}>
        <input
          id={id}
          className="switch"
          type="checkbox"
          checked={enabled}
          onChange={(e) => onToggle(e.target.checked)}
        />
        <span style={{ minWidth: 0 }}>
          <span className="policy-title">{title}</span>
          <span className="policy-note">{summary}</span>
        </span>
      </label>

      {enabled && <div className="policy-body">{children}</div>}
    </div>
  )
}

/** A labelled number input that never lets an empty box become NaN. */
function NumberField({
  label,
  help,
  value,
  min,
  max,
  onChange,
}: {
  label: string
  help?: string
  value: number
  min: number
  max: number
  onChange: (next: number) => void
}) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <input
        className="input"
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const next = Number(e.target.value)
          onChange(Number.isFinite(next) ? Math.min(max, Math.max(min, next)) : min)
        }}
      />
      {help && <p className="field-help">{help}</p>}
    </div>
  )
}

export function RateLimitFields({
  value,
  onChange,
}: {
  value: RateLimitPolicy
  onChange: (next: RateLimitPolicy) => void
}) {
  return (
    <PolicySection
      id="policy-rate-limit"
      title="Rate Limit Policy"
      summary="Specify the throttle limit."
      enabled={value.enabled}
      onToggle={(enabled) => onChange({ ...value, enabled })}
    >
      <div className="field-pair">
        <NumberField
          label="Requests"
          value={value.requests}
          min={1}
          max={100000}
          onChange={(requests) => onChange({ ...value, requests })}
        />
        <NumberField
          label="Per (seconds)"
          value={value.per_seconds}
          min={1}
          max={3600}
          onChange={(per_seconds) => onChange({ ...value, per_seconds })}
        />
      </div>
      <NumberField
        label="Concurrent requests"
        help="How many calls may be in flight at once."
        value={value.concurrency}
        min={1}
        max={64}
        onChange={(concurrency) => onChange({ ...value, concurrency })}
      />
      <p className="field-help">
        {value.requests} request{value.requests === 1 ? '' : 's'} every {value.per_seconds} second
        {value.per_seconds === 1 ? '' : 's'}, at most {value.concurrency} at a time.
      </p>
    </PolicySection>
  )
}

export function CircuitBreakerFields({
  value,
  onChange,
}: {
  value: CircuitBreakerPolicy
  onChange: (next: CircuitBreakerPolicy) => void
}) {
  return (
    <PolicySection
      id="policy-circuit-breaker"
      title="Circuit Breaker Settings"
      summary="Prevent from repeatedly making requests to a failing or unresponsive API."
      enabled={value.enabled}
      onToggle={(enabled) => onChange({ ...value, enabled })}
    >
      <div className="field-pair">
        <NumberField
          label="Failures before opening"
          value={value.failure_threshold}
          min={1}
          max={100}
          onChange={(failure_threshold) => onChange({ ...value, failure_threshold })}
        />
        <NumberField
          label="Cooldown (seconds)"
          value={value.cooldown_seconds}
          min={1}
          max={3600}
          onChange={(cooldown_seconds) => onChange({ ...value, cooldown_seconds })}
        />
      </div>
      <p className="field-help">
        After {value.failure_threshold} consecutive failures, calls stop for{' '}
        {value.cooldown_seconds} seconds before one is let through to see whether the provider has
        recovered.
      </p>
    </PolicySection>
  )
}
